import { Component, OnInit, OnDestroy, HostBinding, ChangeDetectorRef } from "@angular/core";
import {
  AddressSummary,
  AllAddressType
} from "@onsip/common/services/api/resources/addressSummary/address-summary";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { AdvAnnouncement } from "@onsip/common/services/api/resources/announcement/adv-announcement";
import { AdvAnnouncementService } from "@onsip/common/services/api/resources/announcement/adv-announcement.service";
import { map } from "rxjs/operators";
import { Subscription, combineLatest, BehaviorSubject } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { BreakpointObserver } from "@angular/cdk/layout";
import { FormatSingularAddressTypePipe } from "@onsip/web/features/shared/pipes/format-singular-address-type.pipe";
import { RecordingService } from "@onsip/common/services/api/resources/recording/recording.service";
import { DynamicPricingService } from "@onsip/common/services/dynamic-pricing.service";
import { ANNOUNCEMENTS_HANGUP_AOR } from "../../addressAutocomplete/helpers/generate-announcements.config";
import { UserAliasService } from "@onsip/common/services/api/resources/userAlias/user-alias.service";
import { AppAddressService } from "@onsip/common/services/api/resources/appAddress/app-address.service";
import { AppAddress } from "@onsip/common/services/api/resources/appAddress/app-address";
import { AdminTableAbstractComponent } from "@onsip/web/features/abstractClass/admin-table-abstract.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";

export interface AdvAnnouncementWithExt extends AdvAnnouncement {
  extensions: Array<string>;
  appAddress: AppAddress;
}

@Component({
  selector: "onsip-admin-announcements",
  templateUrl: "./adv-announcements-page.component.html",
  styleUrls: ["./adv-announcements-page.component.scss"],
  providers: [FormatSingularAddressTypePipe],
  standalone: false
})
export class AdvAnnouncementsPageComponent
  extends AdminTableAbstractComponent<AdvAnnouncement>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;

  addressSummaries: Array<AddressSummary> = [];
  typeMap: Map<string, AllAddressType> = new Map<string, AllAddressType>();
  nameMap: Map<string, string> = new Map<string, string>();
  hasAnnouncements = new BehaviorSubject<boolean>(true);
  pricingText = "";
  E2eLocators = E2eLocators;

  protected displayedColumnsRecord = {
    XSmall: ["name", "actions"],
    Small: ["name", "transferAddressUsername", "actions"],
    Medium: ["name", "transferAddressUsername", "actions"],
    Large: ["name", "transferAddressUsername", "actions"],
    XLarge: ["name", "transferAddressUsername", "actions"]
  };
  protected unsubscriber = new Subscription();

  constructor(
    private announcementService: AdvAnnouncementService,
    private snackbar: SnackbarService,
    private addressSummaryService: AddressSummaryService,
    private singularAddressTypePipe: FormatSingularAddressTypePipe,
    private recordingService: RecordingService,
    private dynamicPricing: DynamicPricingService,
    private userAliasService: UserAliasService,
    private appAddressService: AppAddressService,
    protected breakpointObserver: BreakpointObserver,
    protected translate: TranslateService,
    protected dialog: MatDialog,
    protected cdr: ChangeDetectorRef
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setDataFilter();
    this.setPricing();
  }

  trackByAnnouncementId(index: number, item: AdvAnnouncement): string {
    return item.advAnnouncementId;
  }

  getTransferText(announcement: AdvAnnouncement): string | undefined {
    const type = this.getAddressType(announcement);
    if (announcement.transferAddressId) {
      if (type && this.nameMap.get(announcement.transferAddressId)) {
        return (
          "(" +
          this.singularAddressTypePipe.transform(type) +
          ") " +
          this.nameMap.get(announcement.transferAddressId)
        );
      } else if (this.nameMap.get(announcement.transferAddressId)) {
        return this.nameMap.get(announcement.transferAddressId);
      }
    }
    return ANNOUNCEMENTS_HANGUP_AOR;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  protected getItemValueByColumn(item: AdvAnnouncement, columnName: string): string | number {
    switch (columnName) {
      case "name":
        return item.name.trim().toLowerCase();
      case "transferAddressUsername":
        return (item?.transferAddressUsername || "").trim().toLowerCase();
      default:
        return parseInt(item.advAnnouncementId);
    }
  }

  protected performDeleteRequest(announcement: AdvAnnouncement): void {
    this.announcementService.advAnnouncementDelete(announcement.advAnnouncementId).then(() => {
      this.setPricing();
      this.snackbar.openSnackBar(
        this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_DELETED", { value: "Announcement" }),
        "success"
      );
    });
  }

  protected getItemName({ name }: AdvAnnouncement): string {
    return name;
  }

  protected initDataSource(): void {
    this.addressSummaryService.addressSummaryBrowse();
    this.announcementService.advAnnouncementBrowse();
    this.userAliasService.userAliasBrowse();
    this.appAddressService.appAddressBrowse();
    this.recordingService.recordingBrowse();

    this.unsubscriber.add(
      combineLatest({
        announcementsArray: this.announcementService.orgState,
        addressArray: this.addressSummaryService.orgState,
        userAliases: this.userAliasService.orgState,
        appAddresses: this.appAddressService.orgState
      })
        .pipe(
          map(({ announcementsArray, addressArray, userAliases, appAddresses }) => {
            announcementsArray.map(announcement => {
              if (announcement.transferAddressId) {
                this.setNameFromId(announcement.transferAddressId, addressArray);
                this.setTypeFromId(announcement.transferAddressId, addressArray);
              }
            });
            const announcementsWithAddress = announcementsArray.map(announcement => ({
              ...announcement,
              appAddress: appAddresses.find(appAddress => appAddress.appId === announcement.appId)
            }));
            return announcementsWithAddress.map(announcement => ({
              ...announcement,
              extensions: userAliases
                .filter(alias => alias.addressId === announcement?.appAddress?.addressId)
                .map(alias => alias.aliasUsername)
            }));
          })
        )
        .subscribe(announcementsArray => {
          this.hasAnnouncements.next(announcementsArray.length > 0);
          this.dataSource.data = announcementsArray;
          this.isDataObtained$.next(true);
        })
    );
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      // name
      const name = data.name;
      // transfer name
      const transferName = this.getTransferText(data) || "";

      return (
        name.toLowerCase().includes(filterValue) || transferName.toLowerCase().includes(filterValue)
      );
    };
  }

  private setPricing(): void {
    this.dynamicPricing.getDynamicPricing("AdvAnnouncement", true).then(result => {
      this.pricingText = result;
      this.cdr.markForCheck();
    });
  }

  private setNameFromId(id: string, addresses: Array<AddressSummary>): void {
    let addressName: string | undefined = "";
    addressName = addresses.find(address => address.addressId === id)?.name;
    if (addressName) {
      this.nameMap.set(id, addressName);
    }
  }

  private setTypeFromId(id: string, addresses: Array<AddressSummary>): void {
    const addressType = addresses.find(address => address.addressId === id)?.addressType;
    if (addressType) {
      this.typeMap.set(id, addressType);
    }
  }

  private getAddressType(announcement: AdvAnnouncement): AllAddressType | undefined {
    return announcement.transferAddressId
      ? this.typeMap.get(announcement.transferAddressId)
      : undefined;
  }
}
